'use client'

import { useEffect, useId, useRef, useState } from 'react'

import clsx from 'clsx'

import type { JSX, KeyboardEvent, ReactNode } from 'react'

type ComboboxProps<T> = {
  label: string
  minChars: number
  fetchOptions: (query: string) => Promise<T[]>
  getOptionLabel: (option: T) => string
  inputValue: string
  onInputChange: (value: string) => void
  onSelect: (option: T) => void
  error?: string
  placeholder?: string
  /** Decorative, at the left inside the field; hidden from assistive tech. */
  icon?: ReactNode
}

/** The live site's react-bootstrap-typeahead AsyncTypeahead waits this long by default (its `delay` prop). */
const LOOKUP_DELAY_MS = 200

/**
 * Box and type shared by the input and its ghost layer, set on both because a native <input> does not inherit
 * font and Preflight is absent: any difference here moves the ghost off the typed text. 16px on phones only, not a
 * scale token: iOS Safari zooms the page into any field under 16px on focus. From 900px the height and type mirror
 * the Search button (`lg` in button.tsx).
 */
const FIELD =
  'box-border h-11 min-[900px]:h-9 rounded-lg border px-3 py-0 font-sans text-[16px] min-[900px]:text-base font-normal leading-normal tracking-normal'

/**
 * A WAI-ARIA 1.2 combobox with an async listbox of suggestions. The text is controlled by the parent:
 * picking an option calls `onSelect` only, and the parent decides what the input shows afterwards.
 */
export default function Combobox<T>({
  label,
  minChars,
  fetchOptions,
  getOptionLabel,
  inputValue,
  onInputChange,
  onSelect,
  error,
  placeholder,
  icon,
}: ComboboxProps<T>): JSX.Element {
  const id = useId()
  const labelId = `${id}-label`
  const listId = `${id}-listbox`
  const errorId = `${id}-error`
  const optionId = (index: number): string => `${id}-option-${index}`

  const [options, setOptions] = useState<T[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  // Only the answer to the latest query may fill the list; slower, older answers are dropped.
  const latestRequest = useRef(0)
  // Set by Escape, Tab, Enter and blur: an answer landing afterwards is kept for reopen() but does not open the list.
  const dismissed = useRef(false)
  // The lookup waiting for the user to pause typing. Escape and blur leave it running, for reopen() to show.
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => (): void => clearTimeout(timer.current), [])

  const expanded = open && options.length > 0

  // Ghost completion, as in the legacy typeahead: the rest of the highlighted (or first) option, if it extends the text.
  const candidate = expanded ? options[active >= 0 ? active : 0] : undefined
  const candidateLabel = candidate === undefined ? '' : getOptionLabel(candidate)
  const ghost =
    inputValue &&
    candidateLabel.length > inputValue.length &&
    candidateLabel.toLowerCase().startsWith(inputValue.toLowerCase())
      ? candidateLabel.slice(inputValue.length)
      : ''

  /** The label with the first case-insensitive occurrence of the typed text in bold. */
  const highlight = (optionLabel: string): ReactNode => {
    const query = inputValue.trim()
    const at = query ? optionLabel.toLowerCase().indexOf(query.toLowerCase()) : -1
    if (at < 0) return optionLabel

    return (
      <>
        {optionLabel.slice(0, at)}
        <strong className="font-semibold">{optionLabel.slice(at, at + query.length)}</strong>
        {optionLabel.slice(at + query.length)}
      </>
    )
  }

  const close = (): void => {
    dismissed.current = true
    setOpen(false)
    setActive(-1)
  }

  const select = (option: T): void => {
    onSelect(option)
    // Nothing left to suggest: focusing the filled field again must not reopen the old list, nor a lookup in flight.
    latestRequest.current++
    clearTimeout(timer.current)
    setOptions([])
    close()
  }

  /** Brings back the suggestions already fetched for this text, after Escape, blur or a validation error. */
  const reopen = (): void => {
    // Even with no options yet: an answer still in flight then shows when it lands, as the user came back for it.
    if (inputValue.trim().length >= minChars) setOpen(true)
  }

  const handleChange = (value: string): void => {
    onInputChange(value)
    setActive(-1)
    dismissed.current = false
    const request = ++latestRequest.current
    clearTimeout(timer.current)

    if (value.trim().length < minChars) {
      setOptions([])
      setOpen(false)
      return
    }

    // Until the new answer lands, keep only the options that still match, so none picked now is for older text.
    const query = value.trim().toLowerCase()
    setOptions((previous) => previous.filter((option) => getOptionLabel(option).toLowerCase().includes(query)))

    timer.current = setTimeout(async () => {
      let result: T[] = []
      try {
        result = await fetchOptions(value.trim())
      } catch {
        // A failed lookup just means no suggestions; the user can keep typing.
      }
      if (request !== latestRequest.current) return

      setOptions(result)
      // The user may have highlighted a kept option meanwhile; its index means nothing in the new list.
      setActive(-1)
      if (!dismissed.current) setOpen(true)
    }, LOOKUP_DELAY_MS)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // These close even while collapsed, so a lookup still in flight cannot reopen the list.
    if (event.key === 'Escape' || event.key === 'Tab' || event.key === 'Enter') {
      // With nothing highlighted, Enter falls through to the form and submits it.
      if (event.key === 'Enter' && expanded && active >= 0) {
        event.preventDefault()
        select(options[active])
      } else if (event.key === 'Tab' && ghost && candidate !== undefined) {
        // No preventDefault: focus still moves on to the next field.
        select(candidate)
      } else {
        if (event.key === 'Escape' && expanded) event.preventDefault()
        close()
      }
      return
    }

    if (!expanded) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        reopen()
      }
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const next =
      event.key === 'ArrowDown' ? (active + 1) % options.length : active <= 0 ? options.length - 1 : active - 1
    setActive(next)
    // Focus stays in the input, so the browser does not scroll the list to the highlighted option by itself.
    document.getElementById(optionId(next))?.scrollIntoView({ block: 'nearest' })
  }

  return (
    <div className="relative flex flex-col gap-1 font-sans">
      <label id={labelId} htmlFor={id} className="text-xs font-medium text-ink">
        {label}
      </label>
      {/* The field background lives here, so the ghost layer shows through the transparent input. */}
      <div className="relative rounded-lg bg-surface">
        {icon && (
          <span
            aria-hidden="true"
            className={clsx(
              'pointer-events-none absolute inset-y-0 left-3 flex items-center',
              error ? 'text-destructive' : 'text-ink-muted',
            )}
          >
            {icon}
          </span>
        )}
        {ghost && (
          // Same box, font and padding as the input, so the ghost lines up with the typed text.
          <div
            aria-hidden="true"
            className={clsx(
              FIELD,
              'pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre border-transparent text-ink-muted',
              icon && 'pl-10',
            )}
          >
            <span className="text-transparent">{inputValue}</span>
            <span>{ghost}</span>
          </div>
        )}
        <input
          // No type="text" (the default anyway): global.scss's `input[type='text']` rule outranks utility classes,
          // so it would decide the input's type while the ghost kept ours, moving the ghost off the typed text.
          id={id}
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          placeholder={placeholder}
          value={inputValue}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={reopen}
          onBlur={close}
          className={clsx(
            FIELD,
            'relative w-full border-border bg-transparent text-ink placeholder:text-ink-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
            icon && 'pl-10',
          )}
        />
      </div>
      {expanded && (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          // Keeps focus in the input, so its blur does not close the list before a click or scrollbar drag lands.
          onMouseDown={(event) => event.preventDefault()}
          className="absolute inset-x-0 top-full z-10 mt-1 max-h-64 list-none overflow-auto rounded-lg border border-border bg-surface-raised p-1 shadow-overlay"
        >
          {options.map((option, index) => (
            <li
              key={optionId(index)}
              id={optionId(index)}
              role="option"
              aria-selected={index === active}
              // Pins the name to the label: jsdom's name computation puts a space at the <strong> boundary even with
              // no whitespace in the DOM (it reads every element without a computed display as a block).
              aria-label={getOptionLabel(option)}
              onClick={() => select(option)}
              className={clsx(
                'cursor-pointer rounded-md px-3 py-2 text-base text-ink',
                index === active ? 'bg-fill-strong' : 'hover:bg-fill-subtle',
              )}
            >
              {highlight(getOptionLabel(option))}
            </li>
          ))}
        </ul>
      )}
      {/* Always rendered, one line tall: an error appearing must not push the fields' neighbours down. A live region
          already in the DOM is also announced more reliably than one inserted with its text. */}
      <p id={errorId} role="alert" className="m-0 min-h-[1lh] text-base font-normal text-destructive">
        {error}
      </p>
    </div>
  )
}
